import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { initAnalytics } from './lib/analytics'
import { ErrorBoundary } from './components/ErrorBoundary'
import App from './App'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000, retry: 1 },
  },
})

const TELEGRAM_DARK_BG = '#06050f'

/**
 * Инициализация Telegram WebApp
 * - Применяем themeParams как CSS-переменные
 * - Вызываем ready() и expand()
 */
function initTelegramWebApp() {
  const tg = (window as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;

  if (!tg) {
    console.log('[TG] Telegram WebApp недоступен, запуск в браузере');
    return;
  }

  // Сообщаем Telegram что приложение готово
  tg.ready();

  // Раскрываем на весь экран (убирает серые поля в WebView)
  if (tg.expand) {
    tg.expand();
  }

  // Подгоняем цвет шапки и фона Telegram под тёмную тему приложения
  if (typeof tg.setHeaderColor === 'function') {
    tg.setHeaderColor(TELEGRAM_DARK_BG);
  }
  if (typeof tg.setBackgroundColor === 'function') {
    tg.setBackgroundColor(TELEGRAM_DARK_BG);
  }
  if (typeof tg.setBottomBarColor === 'function') {
    tg.setBottomBarColor(TELEGRAM_DARK_BG);
  }

  // Применяем цветовую тему из Telegram
  applyThemeParams(tg.themeParams);

  // Слушаем изменения темы
  tg.onEvent?.('themeChanged', () => {
    applyThemeParams(tg.themeParams);
  });

  console.log('[TG] WebApp инициализирован:', {
    version: tg.version,
    platform: tg.platform,
    colorScheme: tg.colorScheme,
  });
}

/**
 * Применяем themeParams как CSS-переменные.
 * We keep premium dark theme for the app canvas; only use Telegram for status bar / hints if needed.
 */
function applyThemeParams(params?: TelegramThemeParams) {
  if (!params) return;

  const root = document.documentElement;
  // Premium theme: do not override app background/text with Telegram light theme
  const mapping: Record<string, string> = {
    hint_color: '--text-muted',
    button_text_color: '--btn-text-color',
    secondary_bg_color: '--card-bg',
  };
  for (const [tgKey, cssVar] of Object.entries(mapping)) {
    const value = params[tgKey as keyof TelegramThemeParams];
    if (value) {
      root.style.setProperty(cssVar, value);
    }
  }
  // Keep premium dark bg and text (do not map bg_color / text_color from Telegram)
}

// Типы для Telegram WebApp
interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface TelegramWebApp {
  ready: () => void;
  expand?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  close?: () => void;
  version?: string;
  platform?: string;
  colorScheme?: 'light' | 'dark';
  themeParams?: TelegramThemeParams;
  initData?: string;
  initDataUnsafe?: { user?: { id: number; username?: string } };
  onEvent?: (event: string, callback: () => void) => void;
}

// Premium app shell: force dark "purple glow" theme (body class + prevent Telegram light override)
document.documentElement.classList.add('vibemom-app');
document.body.classList.add('vibemom-app');

console.log('[build]', (import.meta.env.VITE_BUILD_SHA || import.meta.env.VITE_BUILD_TIME || 'no-version'));

// Инициализируем Telegram до рендера React
initTelegramWebApp();
initAnalytics();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>,
)

